import { Dialog, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Routes } from 'app/config/routes/consts';
import { getFtpSettings } from 'entities/Integrations/api';
import { enqueueSnackbar } from 'notistack';
import { FC, useCallback } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { FtpSettingsTypeEnum } from 'entities/Integrations/enums';
import { ColorPalette } from 'shared/consts/colorPalette';
import { Button } from 'shared/ui/Button';
import { CloseModalButton } from 'shared/ui/CloseModalButton';
import { Paper } from 'shared/ui/Paper';
import { CategoryCard } from 'widgets/startRecognition/ui/CategoryCard';
import { Category } from 'widgets/startRecognition/consts/CategoryList';

interface Props {
	isActiveSessions: boolean;
	category: Category;
	handleClose: () => void;
}

export const DocsUploadModal: FC<Props> = ({ isActiveSessions, category, handleClose }) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { data: ftpSettingsData } = useQuery({
		queryKey: ['import-ftp-settings'],
		queryFn: () => getFtpSettings({ ftpSettingsType: FtpSettingsTypeEnum.generalImport }),
	});

	const handleImportDirect = useCallback(() => {
		if (isActiveSessions) {
			return enqueueSnackbar({
				message: t('start_recognition.snackbar.success_import_direct'),
				variant: 'error',
			});
		}
		const modelId = category.customModelId
			? `custom-model-id=${category.customModelId}`
			: category.generativeId
			? `generative-model-id=${category.generativeId}`
			: '';
		navigate(`${Routes.importFilesDirect}?category=${category.id}&${modelId}`);
	}, [isActiveSessions, navigate]);

	const handleImportFromFtp = useCallback(
		(isCompany: boolean) => {
			if (isActiveSessions) {
				return enqueueSnackbar({
					message: t('start_recognition.snackbar.success_import_ftp'),
					variant: 'error',
				});
			}
			if (!ftpSettingsData?.data.host) {
				return enqueueSnackbar({
					message: t('start_recognition.snackbar.setup_ftp'),
					variant: 'error',
				});
			}
			const modelId = category.customModelId
				? `custom-model-id=${category.customModelId}`
				: category.generativeId
				? `generative-model-id=${category.generativeId}`
				: '';
			navigate(
				`${Routes.importFilesFromFtp}?category=${category.id}&iscompany=${isCompany}&${modelId}`,
			);
		},
		[isActiveSessions, ftpSettingsData, navigate],
	);

	return (
		<>
			<Dialog
				open={true}
				onClose={handleClose}
				PaperComponent={Paper}
				PaperProps={{ title: t('start_recognition.upload_modal.recognition_title') as string }}
				sx={{
					'& .MuiPaper-root': {
						width: '506px',
						maxWidth: '506px',
						overflow: 'inherit',
					},
				}}
			>
				<CloseModalButton action={handleClose} />
				<CategoryCard category={category} />
				<Typography variant={'body1'} color={ColorPalette.baliHai} sx={{ mt: '34px' }}>
					{t('start_recognition.upload_modal.chose_import_variant')}
				</Typography>
				<Button fullWidth sx={{ mt: '24px' }} onClick={handleImportDirect}>
					{t('start_recognition.upload_files')}
				</Button>
				{ftpSettingsData?.data.host && (
					<Button fullWidth sx={{ mt: '24px' }} onClick={() => handleImportFromFtp(false)}>
						{t('start_recognition.upload_modal.import_from_ftp')}
					</Button>
				)}
			</Dialog>
		</>
	);
};
